import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Grid,
    TextField
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PrintIcon from '@mui/icons-material/Print'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'
import { EntityWithIdAndDescription, EntityWithIdAndDescriptionFields } from 'src/types'
import { SistemaPrincipal } from 'src/types/workflow/cache'
import { HttpOperacionInforme } from 'src/pages/sistema/api/httpOperacionInforme'
import { ROUTES } from '../../router'

function ReimpresionInformePage() {
    const navigate = useNavigate()
    const [principales, setPrincipales] = useState<SistemaPrincipal[]>([])
    const [selectedPrincipal, setSelectedPrincipal] = useState<SistemaPrincipal | null>(null)
    const [sistemas, setSistemas] = useState<EntityWithIdAndDescription[]>([])
    const [selectedSistema, setSelectedSistema] = useState<EntityWithIdAndDescription | null>(null)
    const [intIdMensaje, setIntIdMensaje] = useState('')
    const [loadingPrincipales, setLoadingPrincipales] = useState(false)
    const [loadingSistemas, setLoadingSistemas] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        setLoadingPrincipales(true)
        HttpCacheWorkflow.getSistemasPrincipales()
            .then(data => {
                setPrincipales(data)
                if (data.length === 1) setSelectedPrincipal(data[0])
            })
            .finally(() => setLoadingPrincipales(false))
    }, [])

    useEffect(() => {
        setSelectedSistema(null)
        setSistemas([])

        if (!selectedPrincipal) return

        setLoadingSistemas(true)
        HttpCacheWorkflow.getSistemasPorSistemaPrincipal(selectedPrincipal.id)
            .then(data => {
                setSistemas(data)
                if (data.length === 1) setSelectedSistema(data[0])
            })
            .finally(() => setLoadingSistemas(false))
    }, [selectedPrincipal])

    const getErrorMessage = (error: any) => {
        if (typeof error === 'string') return error
        if (error?.descripcionError) return error.descripcionError
        if (error?.DescripcionError) return error.DescripcionError
        if (error?.message) return error.message
        if (error?.title) return error.title

        return 'No se pudo reimprimir'
    }

    const handleSubmit = () => {
        if (!selectedPrincipal || !selectedSistema) return

        const intIdMensajeNumber = Number(intIdMensaje)
        if (!intIdMensajeNumber || intIdMensajeNumber <= 0) return

        setSubmitting(true)
        HttpOperacionInforme.reimprimirInforme({
            tinCodSistemaPrincipal: selectedPrincipal.id,
            sinCodSistema: selectedSistema.id,
            intIdMensaje: intIdMensajeNumber
        }).then(response => {
            if (response?.tieneError) {
                toast.error(response.descripcionError || 'No se pudo reimprimir')

                return
            }

            toast.success('Informe reimpreso y anexado')
        }).catch(error => {
            toast.error(getErrorMessage(error))
        }).finally(() => setSubmitting(false))
    }

    const submitDisabled =
        submitting || !selectedPrincipal || !selectedSistema || !Number(intIdMensaje) || Number(intIdMensaje) <= 0

    return (
        <>
            <Box sx={{ mb: 3 }}>
                <Button variant='text' startIcon={<ArrowBackIcon />} onClick={() => navigate(`/${ROUTES.ADMIN_MASTER}`)}>
                    Volver
                </Button>
            </Box>

            <Card>
                <CardHeader title='Reimpresion' />
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                options={principales}
                                value={selectedPrincipal}
                                loading={loadingPrincipales}
                                onChange={(_, value) => setSelectedPrincipal(value)}
                                getOptionLabel={(option: SistemaPrincipal) =>
                                    option[EntityWithIdAndDescriptionFields.Description] || ''
                                }
                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                                size='small'
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label='Pais *'
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingPrincipales ? <CircularProgress color='inherit' size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                disabled={!selectedPrincipal}
                                options={sistemas}
                                value={selectedSistema}
                                loading={loadingSistemas}
                                onChange={(_, value) => setSelectedSistema(value)}
                                getOptionLabel={(option: EntityWithIdAndDescription) =>
                                    option[EntityWithIdAndDescriptionFields.Description] || ''
                                }
                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                                size='small'
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label='Sistema *'
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingSistemas ? <CircularProgress color='inherit' size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size='small'
                                type='number'
                                label='Id mensaje *'
                                value={intIdMensaje}
                                onChange={event => setIntIdMensaje(event.target.value)}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant='contained'
                                    startIcon={submitting ? <CircularProgress color='inherit' size={18} /> : <PrintIcon />}
                                    disabled={submitDisabled}
                                    onClick={handleSubmit}
                                >
                                    Reimprimir
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </>
    )
}

export default ReimpresionInformePage
